import React, { useMemo, useCallback, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { debounce } from "lodash";
import { useParams } from "react-router-dom";
import { Trans } from "react-i18next";
import { t } from "i18next";
import {
  Checkbox,
  Col,
  Form,
  Input,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  Table,
  Tooltip,
  Button,
  Collapse,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { ReactComponent as RupeesSign } from "../../image/RupeesSign.svg";
import SiteSelectionModal from "./../../feature/PR/SiteSelectionModal";
import { getRequest } from "../../axios";
import { ErrorNotificationMsg, SuccessNotificationMsg } from "../../utils/Notifications";

const { Panel } = Collapse;

const CategoryForm = ({
  item,
  subIndex,
  handleSelectAllChange,
  handleCheckBoxTypename,
  handleAddressChange,
  handleEstimatedPriceChange,
  handleERPCodeChange,
  estimatedPrices,
  erpCodes,
  unitOfMeasurement,
  colors,
  selectAllChecked,
  categoryIndex,
  cat,
  priceMode,
  setPriceMode,
  handleDiameterPriceChange,
  diameterPrices,
  setState,
}) => {
  const { id: materialMasterId } = useParams();
  const [siteModal, setSiteModal] = useState(false);
  const [diameterModalVisible, setDiameterModalVisible] = useState({});
  const [selectedMaterialDetail, setSelectedMaterialDetail] = useState(null);
  const [commonPrice, setCommonPrice] = useState("0");
  const [uniqueSizes, setUniqueSizes] = useState([]);
  const [activeCollapseKey, setActiveCollapseKey] = useState(
    subIndex === 0 ? ["1"] : []
  );

  const transformedSubCategory = useMemo(
    () => item.sub_category?.toUpperCase(),
    [item.sub_category]
  );

  const isSteelCategory = useMemo(() => {
    const isSteel =
      cat?.category_tree_name?.toLowerCase().includes("steel") ||
      item.sub_category?.toLowerCase().includes("tmt");

    return isSteel;
  }, [cat?.category_tree_name, item.sub_category]);

  const filteredUoMNoKG = useMemo(
    () => unitOfMeasurement.filter((i) => i.unit_of_measurement !== "KG"),
    [unitOfMeasurement]
  );

  const filteredUoMNoBag = useMemo(
    () => unitOfMeasurement.filter((i) => i.unit_of_measurement !== "Bag"),
    [unitOfMeasurement]
  );

  const debouncedHandleSelectAllChange = useCallback(
    debounce((e, categoryIndex, subIndex) => {
      handleSelectAllChange(e, categoryIndex, subIndex);
    }, 300),
    [handleSelectAllChange]
  );  

  const debouncedHandleCheckBoxTypename = useCallback(
    debounce((siteIndex, categoryIndex, subIndex, detailIndex, checked) => {
      handleCheckBoxTypename(
        siteIndex,
        categoryIndex,
        subIndex,
        detailIndex,
        checked
      );
    }, 300),
    [handleCheckBoxTypename]
  );

  const debouncedHandleEstimatedPriceChange = useCallback(
    debounce((categoryIndex, subIndex, detailIndex, value) => {
      handleEstimatedPriceChange(categoryIndex, subIndex, detailIndex, value);
    }, 10),
    [handleEstimatedPriceChange]
  );

  const debouncedHandleERPCodeChange = useCallback(
    debounce((categoryIndex, subIndex, detailIndex, value) => {
      handleERPCodeChange(categoryIndex, subIndex, detailIndex, value);
    }, 10),
    [handleERPCodeChange]
  );

  const debouncedHandleAddressChange = useCallback(
    debounce((categoryIndex, subIndex, detailIndex, value) => {
      handleAddressChange(categoryIndex, subIndex, detailIndex, value);
    }, 10),
    [handleAddressChange]
  );

  useEffect(() => {
    if (!materialMasterId) {
      const getDiameter = async () => {
        try {
          const apiResponse = await getRequest("diameter/");
          if (apiResponse.data.success) {
            const sortedSizes = apiResponse.data.data.sort(
              (a, b) => a.ordering - b.ordering
            );
            setUniqueSizes(sortedSizes);
          }
        } catch (error) {
          ErrorNotificationMsg(t("failed_to_fetch_diameters"));
        }
      };
      getDiameter();
    }
  }, [t, materialMasterId]);

  useEffect(() => {
    if (item?.material_details) {
      const initialPriceMode = {};
      item.material_details.forEach((detail, index) => {
        initialPriceMode[index] = detail.pricing_type || "common";

        if (detail.diameter_prices && Array.isArray(detail.diameter_prices)) {
          const diameterPricesObj = {};
          detail.diameter_prices.forEach((dp) => {
            if (dp.diameter_value !== null) {
              const formattedDiameterValue = Number(dp.diameter_value).toFixed(
                2
              );
              diameterPricesObj[formattedDiameterValue] =
                dp.estimated_price?.toString() || "0";
            }
          });

          setState((prevState) => {
            const newState = { ...prevState };
            const materialDetail =
              newState?.site[0]?.category_wise[categoryIndex].company_material_master[
                subIndex
              ].material_details[index];

              materialDetail.diameter_prices = diameterPricesObj;
              return newState;
          });
        }
      });
      setPriceMode(initialPriceMode);
    }
  }, [item?.material_details, categoryIndex, subIndex, setState]);

  const handlePriceChange = useCallback(
    (detailItemIndex, diameterValue, price) => {
      if (/^\d*\.?\d*$/.test(price) || price === "") {
        const formattedDiameterValue = Number(diameterValue).toFixed(2);

        handleDiameterPriceChange(
          categoryIndex,
          subIndex,
          detailItemIndex,
          "pricing_type",
          "diameter_wise"
        );

        // Find the diameter object from uniqueSizes to get the diameter_id
        const diameterObj = uniqueSizes.find(size => 
          Number(size.diameter_value || size.value).toFixed(2) === formattedDiameterValue
        );

        handleDiameterPriceChange(
          categoryIndex,
          subIndex,
          detailItemIndex,
          formattedDiameterValue,
          {
            estimated_price: price,
            diameter_id: diameterObj?.id || null,
            diameter_uom: diameterObj?.diameter_uom || 'mm'
          }
        );

        setState((prevState) => {
          const newState = { ...prevState };
          const materialDetail =
            newState?.site[0]?.category_wise[categoryIndex].company_material_master[
              subIndex
            ].material_details[detailItemIndex];

          if (!materialDetail.diameter_prices) {
            materialDetail.diameter_prices = {};
          }

          materialDetail.diameter_prices[formattedDiameterValue] = {
            estimated_price: price,
            diameter_id: diameterObj?.id || null,
            diameter_uom: diameterObj?.diameter_uom || 'mm'
          };
          materialDetail.pricing_type = "diameter_wise";

          return newState;
        });
      }
    },
    [handleDiameterPriceChange, categoryIndex, subIndex, setState, uniqueSizes]
  );

  const handleCommonPriceChange = useCallback(
    (value) => {
      if (/^\d*\.?\d*$/.test(value) || value === "") {
        setCommonPrice(value);
        if (selectedMaterialDetail) {
          handleDiameterPriceChange(
            categoryIndex,
            subIndex,
            selectedMaterialDetail.detailIndex,
            "pricing_type",
            "common"
          );

          setState((prevState) => {
            const newState = { ...prevState };
            const materialDetail =
              newState?.site[0]?.category_wise[categoryIndex].company_material_master[
                subIndex
              ].material_details[selectedMaterialDetail.detailIndex];

            materialDetail.estimated_price = value;
            materialDetail.diameter_prices = [];
            materialDetail.pricing_type = "common";

            return newState;
          });
        }
      }
    },
    [
      categoryIndex,
      subIndex,
      selectedMaterialDetail,
      handleDiameterPriceChange,
      setState,
    ]
  );

  const handlePriceModeChange = useCallback(
    (e, detailIndex) => {
      const newMode = e.target.value;
      setPriceMode((prev) => ({
        ...prev,
        [detailIndex]: newMode,
      }));

      if (selectedMaterialDetail) {
        setState((prevState) => {
          const newState = { ...prevState };
          const materialDetail =
            newState?.site[0]?.category_wise[categoryIndex].company_material_master[
              subIndex
            ].material_details[detailIndex];

          materialDetail.pricing_type =
            newMode === "common" ? "common" : "diameter_wise";

          if (newMode === "common") {
            if (!materialDetail.estimated_price) {
              setCommonPrice("0");
              // materialDetail.estimated_price = "0";
            } else {
              setCommonPrice(materialDetail.estimated_price.toString());
            }
            // materialDetail.diameter_prices = [];
          }

          return newState;
        });
      }
    },
    [categoryIndex, subIndex, selectedMaterialDetail, setState]
  );


  const handleOpenDiameterModal = useCallback((detail) => {
    setSelectedMaterialDetail(detail);
    setDiameterModalVisible({
      [detail.detailIndex]: true,
    });

    // Set price mode based on pricing_type
    const currentPricingType = detail.detailData?.pricing_type || "common";
    setPriceMode(prev => ({
      ...prev,
      [detail.detailIndex]: currentPricingType
    }));

    if (currentPricingType === "common") {
      setCommonPrice(detail.detailData.estimated_price?.toString() || "0");
    }
  }, []);

  const closeDiameterModal = useCallback((detailIndex) => {
    setDiameterModalVisible({
      [detailIndex]: false,
    });
    setSelectedMaterialDetail(null);
    // SuccessNotificationMsg("Saved as draft");
  }, []);

  const handleCollapseChange = useCallback((key) => {
    setActiveCollapseKey(key);
  }, []);

  const formatToIndianNumberSystem = useCallback((value) => {
    if (!value) return "";
    const number = Number(value.toString().replace(/,/g, ""));
    return isNaN(number) ? value : number.toLocaleString("en-IN");
  }, []);

  const dataSource = useMemo(() => {
    if (!selectedMaterialDetail) return [];

    const materialDetail = item.material_details[selectedMaterialDetail.detailIndex];
    const existingDiameterPrices = materialDetail?.diameter_prices || {};

    const allDiameters = new Set();
    const diameterUomMap = new Map();

    // Add diameters from existing prices
    Object.keys(existingDiameterPrices).forEach(diameterValue => {
      if (!isNaN(Number(diameterValue))) {
        const formattedValue = Number(diameterValue).toFixed(2);
        allDiameters.add(formattedValue);
        diameterUomMap.set(formattedValue, "mm");
      }
    });

    // Add diameters from uniqueSizes
    uniqueSizes.forEach((sizeObj) => {
      const diameterValue = materialMasterId
        ? sizeObj?.diameter_value
        : sizeObj?.value;
      if (diameterValue && !isNaN(Number(diameterValue))) {
        const formattedValue = Number(diameterValue).toFixed(2);
        allDiameters.add(formattedValue);
        if (!diameterUomMap.has(formattedValue)) {
          diameterUomMap.set(formattedValue, sizeObj?.diameter_uom || "mm");
        }
      }
    });

    return Array.from(allDiameters)
      .sort((a, b) => Number(a) - Number(b))
      .map((diameterValue, index) => {
        const priceData = existingDiameterPrices[diameterValue];
        const price = typeof priceData === 'object' ? priceData.estimated_price : priceData;
        
        return {
          key: index,
          size: diameterValue,
          uom: diameterUomMap.get(diameterValue) || "mm",
          originalDiameterValue: diameterValue,
          price: price || "0",
          diameter_id: typeof priceData === 'object' ? priceData.diameter_id : null,
        };
      });
  }, [uniqueSizes, materialMasterId, selectedMaterialDetail, item?.material_details]);

const columns = useMemo(
    () => [
      {
        title: t("Diameter Size") || "Diameter Size",
        dataIndex: "size",
        key: "size",
        width: "40%",
        render: (text, record) => {
          const formattedSize =
            record.size && record.size.endsWith(".00")
              ? record.size.replace(".00", "")
              : record.size || "";
          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                fontWeight: 500,
                fontSize: "14px",
              }}
            >
              {`${formattedSize} ${record.uom || "mm"}`}
            </div>
          );
        },
      },
      {
        title: t("Estimated Price") || "Price (₹/kg)",
        dataIndex: "price",
        key: "price",
        width: "60%",
        render: (text, record, index) => {
          const isCommonPricing =
            priceMode[selectedMaterialDetail?.detailIndex] === "common";
          const currentPrice = isCommonPricing
            ? commonPrice
            : record.price || "0";
 
          return (
            <Input
              type="number"
              value={currentPrice}
              onChange={(e) => {
                if (isCommonPricing) {
                  handleCommonPriceChange(e.target.value);
                } else {
                  handlePriceChange(
                    selectedMaterialDetail?.detailIndex,
                    record.originalDiameterValue,
                    e.target.value
                  );
                }
              }}
              placeholder={t("pl_estimated_price") || "Enter estimated price"}
              maxLength={15}
              className="TextFeild"
              style={{
                width: "150px",
                borderRadius: "4px",
                border: "1px solid #d9d9d9",
                padding: "4px 11px",
                transition: "all 0.3s",
              }}
              prefix={<RupeesSign style={{ color: "#8c8c8c" }} />}
              // disabled={
              //   isCommonPricing &&
              //   record.originalDiameterValue !== uniqueSizes[0]?.value
              // }
              disabled
            />
          );
        },
      },
    ],
    [
      t,
      priceMode,
      selectedMaterialDetail,
      commonPrice,
      handleCommonPriceChange,
      handlePriceChange,
      uniqueSizes,
    ]
  );

  const midPoint = Math.ceil(dataSource.length / 2);
  const firstHalfData = dataSource.slice(0, midPoint);
  const secondHalfData = dataSource.slice(midPoint);

  const renderedMaterials = useMemo(() => {
    const uniqueMaterials = new Set();
    console.log("iteeeem", item);
    
    const dataSource = item?.material_details
      ?.filter((detail, detailIndex) => {
        const materialKey = `${detail.material_type_name}-${detail.unit_of_measurement_name}`;
        if (uniqueMaterials.has(materialKey)) {
          return false;
        }
        uniqueMaterials.add(materialKey);
        return true;
      })
      .map((detail, detailIndex) => {
        return {
          sr_no: detailIndex + 1,
          key: detailIndex,
          sub_category: item.sub_category,
          material_type: detail.material_type_name,
          preferred_uom: detail.unit_of_measurement_name,
          estimated_price: detail.estimated_price,
          procem_code: detail.smart_buyer_code,
          erp_code: detail.company_material_code,
          is_checked: detail.is_checked,
          detailData: {
            ...detail,
            pricing_type: detail.pricing_type || "common",
          },
          detailIndex: detailIndex,
        };
      });

    const columns = [
      {
        title: (
          <Checkbox
            onChange={(e) => handleSelectAllChange(e, categoryIndex, subIndex)}
            checked={selectAllChecked[`${categoryIndex}_${subIndex}`] || false}
            onClick={(e) => e.stopPropagation()}
          />
        ),
        dataIndex: "is_checked",
        key: "checkbox",
        render: (text, record) => (
          <Checkbox
            onChange={(e) =>
              handleCheckBoxTypename(
                0,
                categoryIndex,
                subIndex,
                record.detailIndex,
                e.target.checked
              )
            }
            checked={record.is_checked}
          />
        ),
      },
      {
        title: "Sr.",
        dataIndex: "sr_no",
        key: "sr_no",
      },
      {
        title: "Sub Category",
        dataIndex: "sub_category",
        key: "sub_category",
        onCell: (_, index) => {
          const firstRowIndex = dataSource.findIndex(
            (row) => row.sub_category === dataSource[index].sub_category
          );
          const rowSpan = dataSource.filter(
            (row) => row.sub_category === dataSource[index].sub_category
          ).length;
          if (index === firstRowIndex) {
            return { rowSpan };
          }
          return { rowSpan: 0 };
        },
      },
      {
        title: "Material Type",
        dataIndex: "material_type",
        key: "material_type",
      },
      {
        title: "Preferred UoM.",
        dataIndex: "preferred_uom",
        key: "preferred_uom",
        render: (text, record) => {
          const value = record.preferred_uom;
          if (!isSteelCategory && record.sub_category === "RMC") {
            return (
              <Form.Item noStyle>
                <span>{value === undefined || value === null || value === "" ? "N/A" : "Cu. Mtr."}</span>
              </Form.Item>
            );
          } else {
            return (
              <Form.Item noStyle>
                <span>{value === undefined || value === null || value === "" ? "N/A" : value}</span>
              </Form.Item>
            );
          }
        },
      },
      {
        title: "Estimated Price",
        dataIndex: "estimated_price",
        key: "estimated_price",
        render: (text, record) => {
          if (isSteelCategory) {
            return (
              <Button
                type="primary"
                onClick={() => {
                  handleOpenDiameterModal(record);
                }}
                style={{
                  backgroundColor: "#094E8F",
                  color: "white",
                  border: "none",
                  padding: "2px 12px",
                  height: "28px",
                  borderRadius: "2px",
                  fontWeight: 400,
                  fontSize: "13px",
                }}
              >
                View Price
              </Button>
            );
          } else {
            const value = estimatedPrices[
              `${categoryIndex}_${subIndex}_${record.detailIndex}`
            ] ?? record.estimated_price;
            return (
              <Form.Item noStyle>
                <span>
                  {value === undefined || value === null || value === "" ? "N/A" : `₹ ${formatToIndianNumberSystem(value)}`}
                </span>
              </Form.Item>
            );
          }
        },
      },
      {
        title: "Procem Code",
        dataIndex: "procem_code",
        key: "procem_code",
        render: (text, record) => (
          <Form.Item noStyle>
            <span>{text === undefined || text === null || text === "" ? "N/A" : text}</span>
          </Form.Item>
        ),
      },
      {
        title: "ERP/ SAP Code",
        dataIndex: "erp_code",
        key: "erp_code",
        render: (text, record) => {
          const value = erpCodes[
            `${categoryIndex}_${subIndex}_${record.detailIndex}`
          ] ?? text;
          return (
            <Form.Item noStyle>
              <span>{value === undefined || value === null || value === "" ? "N/A" : value}</span>
            </Form.Item>
          );
        },
      },
    ];

    return (
      <Table
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        scroll={{ x: true }}
        bordered
        rowKey="key"
      />
    );
  }, [
    item?.material_details,
    item.sub_category,
    categoryIndex,
    subIndex,
    estimatedPrices,
    erpCodes,
    unitOfMeasurement,
    isSteelCategory,
    handleOpenDiameterModal,
    formatToIndianNumberSystem,
    debouncedHandleAddressChange,
    debouncedHandleEstimatedPriceChange,
    debouncedHandleERPCodeChange,
    debouncedHandleCheckBoxTypename,
  ]);




  return (
    <div key={subIndex}>
      <Form>
        <Collapse
          activeKey={activeCollapseKey}
          onChange={handleCollapseChange}
          style={{
            background: "#fff",
            border: "none",
            borderRadius: "4px",
          }}
        >
          <Panel
            header={
              <div
                style={{
                  fontWeight: 600,
                  color: activeCollapseKey.includes("1")
                    ? "white"
                    : "#262626",
                }}
              >
                {transformedSubCategory}
              </div>
            }
            key="1"
            style={{
              border: "1px solid #f0f0f0",
              borderRadius: "4px",
              marginBottom: "16px",
            }}
          >
            <div className="stepsContent">
               <div style={{ marginBottom: "15px" }}>
                {isSteelCategory ? (
                  <>
                  
                    
                    {(() => {
                      const material = item.material_details.find(
                        (material) => material.specification != null
                      );
                      return material ? (
                        <>

                        <span>
                        All Angle as per Specification, Grade and Quality as per 
                          <strong>{" "} {material.specification} {" "}</strong> 
                        </span>
                        {
                          material?.dimension_standard && (
                        <span>
                          and Dimension Standard as per <strong>{material?.dimension_standard}</strong>
                        </span>
                          )
                        }
                        
                        </>
                      )
                      : null;
                    })()}
                  </>
                ) : (
                  ""
                )}
              </div>
                <div className="cardwrapper">{renderedMaterials}</div>
            </div>
          </Panel>
        </Collapse>
      </Form>

      {isSteelCategory && selectedMaterialDetail && (
        <Modal
          title={
            <div
              style={{
                fontSize: "16px",
                fontWeight: 600,
                color: "#262626",
                margin: 0,
                padding: 0,
              }}
            >
              Diameter Wise Pricing - {selectedMaterialDetail.material_type}
            </div>
          }
          open={diameterModalVisible[selectedMaterialDetail.detailIndex]}
          onCancel={() =>
            closeDiameterModal(selectedMaterialDetail.detailIndex)
          }
          footer={[
            <Button
              key="close"
              onClick={() =>
                closeDiameterModal(selectedMaterialDetail.detailIndex)
              }
              style={{
                backgroundColor: "#094E8F",
                color: "white",
                border: "none",
                padding: "2px 12px",
                height: "28px",
                borderRadius: "2px",
                fontWeight: 400,
                fontSize: "13px",
              }}
            >
              Close
            </Button>,
          ]}
          width={800}
          bodyStyle={{
            padding: "12px 12px 24px 12px",
            maxHeight: "80vh",
            overflowY: "auto",
            background: "#fff",
          }}
          style={{ top: 30 }}
          className="diameter-modal-compact"
        >
          <div
            style={{
              backgroundColor: "#f5f5f5",
              padding: "8px 12px",
              borderRadius: "3px",
              marginBottom: "10px",
              fontSize: "12px",
              color: "#888",
              lineHeight: 1.4,
            }}
          >
            View the price for each diameter size. Prices should be in ₹/MT.
          </div>
          <div style={{ marginBottom: "16px" }}>
            <Form.Item label="Price Mode" noStyle>
              <Radio.Group
              disabled
                value={
                  priceMode[selectedMaterialDetail.detailIndex] || "common"
                }
                onChange={(e) =>
                  handlePriceModeChange(e, selectedMaterialDetail.detailIndex)
                }
              >
                <Radio value="common">Common Price</Radio>
                <Radio value="diameter_wise">Diameter Wise</Radio>
              </Radio.Group>
            </Form.Item>
          </div>
          {priceMode[selectedMaterialDetail.detailIndex] ===
            "diameter_wise" && (
            <Row gutter={16}>
              <Col span={12}>
                <Table
                  columns={columns}
                  dataSource={firstHalfData}
                  pagination={false}
                  scroll={{ x: true }}
                  style={{
                    marginTop: 0,
                    width: "100%",
                    borderRadius: "3px",
                    overflow: "hidden",
                    background: "#fff",
                  }}
                  className="diameter-price-table compact"
                  rowClassName={() => "diameter-price-row"}
                  size="middle"
                />
              </Col>
              <Col span={12}>
                <Table
                  columns={columns}
                  dataSource={secondHalfData}
                  pagination={false}
                  scroll={{ x: true }}
                  style={{
                    marginTop: 0,
                    width: "100%",
                    borderRadius: "3px",
                    overflow: "hidden",
                    background: "#fff",
                  }}
                  className="diameter-price-table compact"
                  rowClassName={() => "diameter-price-row"}
                  size="middle"
                />
              </Col>
            </Row>
          )}
          {priceMode[selectedMaterialDetail.detailIndex] === "common" && (
            <Form.Item label="Common Price" style={{ marginBottom: 0 }}>
              <Input
              disabled
                type="number"
                value={commonPrice}
                onChange={(e) => handleCommonPriceChange(e.target.value)}
                placeholder="Enter common price"
                prefix={<RupeesSign />}
                style={{ width: "200px" }}
              />
            </Form.Item>
          )}
        </Modal>
      )}
    </div>
  );
};

CategoryForm.propTypes = {
  item: PropTypes.shape({
    sub_category: PropTypes.string.isRequired,
    specification: PropTypes.string.isRequired,
    dimension_standard: PropTypes.string.isRequired,
    material_details: PropTypes.arrayOf(
      PropTypes.shape({
        is_checked: PropTypes.bool.isRequired,
        material_type_name: PropTypes.string.isRequired,
        smart_buyer_code: PropTypes.string,
        unit_of_measurement: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]),
        unit_of_measurement_name: PropTypes.string,
        estimated_price: PropTypes.number,
        company_material_code: PropTypes.string,
        diameter_prices: PropTypes.object,
      })
    ).isRequired,
  }).isRequired,
  subIndex: PropTypes.number.isRequired,
  handleSelectAllChange: PropTypes.func.isRequired,
  handleCheckBoxTypename: PropTypes.func.isRequired,
  handleAddressChange: PropTypes.func.isRequired,
  handleEstimatedPriceChange: PropTypes.func.isRequired,
  handleERPCodeChange: PropTypes.func.isRequired,
  estimatedPrices: PropTypes.object.isRequired,
  erpCodes: PropTypes.object.isRequired,
  unitOfMeasurement: PropTypes.array.isRequired,
  colors: PropTypes.array.isRequired,
  selectAllChecked: PropTypes.object.isRequired,
  categoryIndex: PropTypes.number.isRequired,
  cat: PropTypes.object.isRequired,
  setPriceMode: PropTypes.func.isRequired,
  priceMode: PropTypes.object.isRequired,
  handleDiameterPriceChange: PropTypes.func.isRequired,
  diameterPrices: PropTypes.object.isRequired,
  setState: PropTypes.func.isRequired,
};

export default React.memo(CategoryForm);
